import { Interval } from "./Interval";

export type { Interval } from "./Interval";

export type AssetStatus = "active" | "maintenance" | "spare" | "decommissioned" | "failed" | "disposed";
export type AssetCriticality = "low" | "medium" | "high" | "critical";
export type AssetNoteType = "general" | "maintenance" | "change" | "incident" | "failure" | "security" | "configuration" | "contract" | "installation" | "decommission" | "service_execution";
export type TemplateFieldType = "text" | "textarea" | "number" | "boolean" | "select" | "multiselect" | "date" | "datetime" | "duration" | "percentage" | "temperature" | "voltage" | "current" | "attachment" | "signature" | "pass_fail";
export type AssetCustomPropertyType = "text" | "textarea" | "number" | "boolean" | "date" | "datetime" | "url";

export interface AssetCustomProperty {
    id: string;
    key: string;
    label: string;
    type: AssetCustomPropertyType;
    unit?: string;
    value: unknown;
}

export interface AssetMikrotikInterface {
    name: string;
    label: string;
}

export interface AssetMikrotikPingTarget {
    id: string;
    name: string;
    enabled: boolean;
    asset_id?: string;
    address: string;
}

export interface AssetMikrotikManagement {
    address: string;
    port: number;
    web_url?: string;
    username: string;
    password?: string;
    use_tls: boolean;
    interface?: string;
    interfaces: AssetMikrotikInterface[];
    ping_targets?: AssetMikrotikPingTarget[];
}

export type AssetManagerType = "mikrotik" | "remote_desktop" | "domain_name";

export interface AssetRemoteDesktopManager {
    rport_id: string;
}

export interface AssetDomainNameManager {
    domain_name: string;
}

export interface AssetManager {
    id: string;
    type: AssetManagerType;
    name?: string;
    enabled: boolean;
    external_id?: string;
    mikrotik?: AssetMikrotikManagement;
    remote_desktop?: AssetRemoteDesktopManager;
    domain_name?: AssetDomainNameManager;
}

export interface AssetMikrotikInterfaceTraffic {
    name: string;
    label: string;
    rx_bps: number;
    tx_bps: number;
}

export interface AssetMikrotikPingTargetStatus {
    target_id: string;
    asset_id?: string;
    name: string;
    address: string;
    state: "unknown" | "up" | "degraded" | "unreachable" | "down";
    sent: number;
    received: number;
    packet_loss_percent: number;
    min_rtt_ms: number;
    avg_rtt_ms: number;
    max_rtt_ms: number;
    last_attempt_at?: string;
    last_success_at?: string;
    state_changed_at?: string;
    consecutive_failures: number;
    stale: boolean;
    error?: string;
}

export interface AssetMikrotikStatus {
    connected: boolean;
    state?: "connecting" | "online" | "unreachable" | "offline" | "authentication_failed" | "configuration_error";
    error?: string;
    identity: string;
    version: string;
    board_name: string;
    architecture: string;
    uptime: string;
    cpu_load: number;
    free_memory: number;
    total_memory: number;
    current_firmware: string;
    upgrade_firmware: string;
    latest_version: string;
    update_status: string;
    update_available: boolean;
    interface: string;
    rx_bps: number;
    tx_bps: number;
    interfaces: AssetMikrotikInterfaceTraffic[];
    ping_targets?: AssetMikrotikPingTargetStatus[];
    retrieved_at: string;
    last_attempt_at?: string;
    last_success_at?: string;
    state_changed_at?: string;
    consecutive_failures?: number;
    sequence?: number;
}

export interface AssetMikrotikManagerStatus {
    asset: Asset;
    manager_id: string;
    router: AssetMikrotikStatus;
}

export interface AssetDomainNameStatus {
    connected: boolean;
    error?: string;
    domain_name: string;
    handle?: string;
    status: string[];
    nameservers: string[];
    registered_at?: string;
    expires_at?: string;
    last_changed_at?: string;
    days_until_expiration?: number;
    retrieved_at: string;
}

export interface AssetDomainNameManagerStatus {
    asset_id: string;
    asset_name: string;
    manager_id: string;
    domain: AssetDomainNameStatus;
}

export interface AssetRemoteDesktopStatus {
    id: string;
    name: string;
    address: string;
    hostname: string;
    os: string;
    os_kernel: string;
    cpu_model_name: string;
    connection_state: string;
    updates_status?: { updates_available: number };
    vnc_token: string;
}

export type AssetManagerStatus = AssetMikrotikManagerStatus | AssetDomainNameManagerStatus | AssetRemoteDesktopStatus;

export interface AssetDocumentReference {
    document_id: string;
    link_id: string;
    type: string;
    name: string;
    content_type?: string;
    size?: number;
    sha256?: string;
    signature_required: boolean;
    signed_at?: string;
    signed_by?: string;
    signed_document_id?: string;
}

export interface AssetTemplateField {
    id: string;
    key: string;
    label: string;
    type: TemplateFieldType;
    required: boolean;
    unit?: string;
    options?: string[];
    order: number;
}

export interface AssetNoteTemplate {
    name: string;
    fields: AssetTemplateField[];
}

export interface AssetTemplateValue {
    field_id: string;
    value: unknown;
}

export interface AssetIntervalScript {
    type: "dummy";
    enabled: boolean;
    config?: Record<string, unknown>;
    last_run_at?: string;
    last_result?: string;
}

export interface AssetServiceInterval {
    id: string;
    name: string;
    interval: Interval;
    enabled: boolean;
    grace_period_days: number;
    priority: AssetCriticality;
    assigned_team: string;
    template?: AssetNoteTemplate;
    note_ids: string[];
    last_scheduled_at?: string;
    next_scheduled_at?: string;
    last_completed_at?: string;
    last_completed_due_at?: string;
    script?: AssetIntervalScript;
    due: boolean;
    overdue: boolean;
}

export interface Asset {
    id: string;
    tenant_id: string;
    name: string;
    type: string;
    status: AssetStatus;
    customer_id?: string;
    location: string;
    serial_number: string;
    manufacturer: string;
    model: string;
    installation_date?: string;
    purchase_date?: string;
    warranty_until?: string;
    end_of_life_date?: string;
    criticality: AssetCriticality;
    owner: string;
    supplier_id?: string;
    description: string;
    custom_properties: AssetCustomProperty[];
    managers: AssetManager[];
    parent_ids: string[];
    child_ids: string[];
    note_ids: string[];
    service_intervals: AssetServiceInterval[];
    created_at: string;
    updated_at: string;
}

export interface AssetNoteCorrection {
    corrected_at: string;
    corrected_by: string;
    reason: string;
    previous: Record<string, unknown>;
}

export interface AssetNote {
    id: string;
    tenant_id: string;
    asset_id: string;
    customer_id?: string;
    type: AssetNoteType;
    title: string;
    content: string;
    parent_note_id?: string;
    service_interval_id?: string;
    scheduled_at?: string;
    completed_at?: string;
    performed_by?: string;
    result?: string;
    service_interval_snapshot?: {
        name: string;
        interval: Interval;
    };
    template_snapshot?: AssetNoteTemplate;
    values?: AssetTemplateValue[];
    documents: AssetDocumentReference[];
    corrections?: AssetNoteCorrection[];
    created_by: string;
    created_at: string;
    edited_at?: string;
    updated_at: string;
}

export interface AssetFilters {
    tenantId?: string;
    customerId?: string;
    parentId?: string;
    query?: string;
    type?: string;
    status?: string;
    criticality?: string;
}

export const emptyAsset = (): Asset => ({
    id: "",
    tenant_id: "",
    name: "",
    type: "server",
    status: "active",
    location: "",
    serial_number: "",
    manufacturer: "",
    model: "",
    criticality: "medium",
    owner: "",
    description: "",
    custom_properties: [],
    managers: [],
    parent_ids: [],
    child_ids: [],
    note_ids: [],
    service_intervals: [],
    created_at: "",
    updated_at: "",
});
